import { combineLatest, map, Observable, shareReplay, startWith } from 'rxjs';
import { action$, ofType } from '@ardoq/rxbeach';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { toIdSet } from '@ardoq/core';
import { components$ } from 'streams/components/components$';
import { references$ } from 'streams/references/references$';
import { notifyFilterColorChanged } from './FilterActions';

export type IncludedInContext = {
  includedComponentIds: ReadonlySet<ArdoqId>;
  includedReferenceIds: ReadonlySet<ArdoqId>;
};

const computeIncludedInContext = (
  components: readonly APIComponentAttributes[],
  references: readonly APIReferenceAttributes[]
): IncludedInContext => ({
  includedComponentIds: toIdSet(
    components.filter(component =>
      componentInterface.isIncludedInContextByFilter(component)
    )
  ),
  includedReferenceIds: toIdSet(
    references.filter(reference =>
      referenceInterface.isIncludedInContextByFilter(reference)
    )
  ),
});

// notifyFilterColorChanged is dispatched immediately after filterCache.reset(),
// so recomputing on it reads a freshly-reset cache regardless of subscription order.
export const includedInContext$: Observable<IncludedInContext> = combineLatest([
  components$,
  references$,
  action$.pipe(ofType(notifyFilterColorChanged), startWith(null)),
]).pipe(
  map(([components, references]) =>
    computeIncludedInContext(components.models, references.models)
  ),
  shareReplay({ refCount: false, bufferSize: 1 })
);

const EMPTY_INCLUDED_IN_CONTEXT: IncludedInContext = {
  includedComponentIds: new Set(),
  includedReferenceIds: new Set(),
};

// The graph builders rebuild imperatively, outside the stream graph, so they
// cannot observe includedInContext$. Reading its shareReplay buffer here yields
// the current inclusion sets synchronously, independent of subscription order.
export const getCurrentIncludedInContext = (): IncludedInContext => {
  let current = EMPTY_INCLUDED_IN_CONTEXT;
  const subscription = includedInContext$.subscribe(value => {
    current = value;
  });
  subscription.unsubscribe();
  return current;
};
